import { useContext, type SyntheticEvent } from 'react';
import type { useExpressComponents } from '../../../hooks/useExpressComponents';
import { Seprator } from '@/components/Payment/Seprator';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useIsWalletZipTaxActive } from '@/hooks/useIsWalletZipTaxActive';
import { useShouldBlockExpressCheckoutButtons } from '@/hooks/useShouldBlockExpressCheckoutButtons';
import { cn } from '@/utils/cn';
import { TemplateType } from '@/types/boot';

interface ExpressCheckoutRendererProps {
  onDisabledButtonClicked: () => void;
  blockedForZipCode: boolean;
  isZipCodeValid?: boolean;
  /**
   * Owned by `<Payment>` rather than this component: wallet readiness also
   * decides when CHECKOUT_PRESENTED fires and which methods it reports, and
   * `useExpressComponents` keeps its state locally — a second call here would
   * be a separate copy that never receives any `onReady` report.
   */
  expressState: ReturnType<typeof useExpressComponents>;
}

/**
 * Lays out the express wallet row.
 *
 * Each wallet owns its own loading UX (`WalletButtonShell` paints a
 * brand-styled placeholder + loader overlay from t=0), so the renderer
 * no longer hides everything behind a shared skeleton. Wallets reveal
 * independently as each PSP reports ready — same placement, no layout
 * shift, no all-or-nothing wait.
 *
 * The Separator still waits for `isAllComponentsLoaded` so it doesn't
 * flash before late-arriving wallets settle.
 *
 * ACDEV-25067: when `WALLET_ZIP_TAX` is OFF, we fall back to the pre-branch
 * ZIP-blocker overlay pattern (also used in `Paypal.tsx`) — without it the
 * wallet iframe opens for a frame and closes when our `handleClick`
 * rejects, which shows as a visible flicker on SINGLE/PREFERRED. When
 * the FF is ON, ZIP is captured inside the wallet sheet so the overlay
 * is unnecessary.
 */
export const ExpressCheckoutRenderer = ({
  onDisabledButtonClicked,
  blockedForZipCode,
  isZipCodeValid,
  expressState,
}: ExpressCheckoutRendererProps) => {
  const { expressComponents, handleComponentReady, isAllComponentsLoaded, isComponentsAvailable } = expressState;
  const { template } = useContext(CheckoutContext);
  // When the wallet-tax pipeline is on (FF or ACDEV-27914 fallback), drop
  // the pre-branch ZIP-blocker overlay because the wallet sheet becomes the
  // ZIP input surface. Otherwise keep the overlay so an unblocked tap
  // through to Stripe/Adyen doesn't briefly open a wallet iframe before the
  // click handler rejects.
  const isWalletZipTaxActive = useIsWalletZipTaxActive();
  const { shouldBlockExpressCheckoutButtons } = useShouldBlockExpressCheckoutButtons();
  const shouldBlockWalletClick = !isWalletZipTaxActive && shouldBlockExpressCheckoutButtons && !isZipCodeValid;

  const handleBlockedWalletClick = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDisabledButtonClicked();
  };

  if (expressComponents.length === 0) {
    return null;
  }

  const showSeparator = isAllComponentsLoaded && isComponentsAvailable && !blockedForZipCode;
  // Reserve the separator's height WHILE the wallets are still loading so that,
  // once they settle, the "Or pay another way" row fades in in place instead of
  // pushing the rest of the sheet down (the visible "jump"). We only collapse
  // the reserved space when we KNOW the separator won't render — after loading
  // with no available wallet, or while ZIP-blocked.
  const reserveSeparatorSpace = !isAllComponentsLoaded && !blockedForZipCode;

  return (
    <div
      data-testid="express-checkout-renderer"
      className={cn('mt-3 md:mt-0 relative', template.type === TemplateType.EMBEDDED_VIEW && 'mt-0')}
    >
      <div className="flex flex-col gap-4 express-checkout-vertical">
        <div className="flex flex-col gap-2">
          {expressComponents.map(({ key, Component }) => (
            <Component
              key={key}
              isExpress
              onInvalidTaxClick={onDisabledButtonClicked}
              onReady={(isAvailable, _reason, availableMethods) =>
                handleComponentReady(key, isAvailable, availableMethods)
              }
            />
          ))}
        </div>
        {(showSeparator || reserveSeparatorSpace) && (
          <div
            className={cn('transition-opacity duration-300', showSeparator ? 'opacity-100' : 'opacity-0')}
            aria-hidden={!showSeparator}
          >
            <Seprator />
          </div>
        )}
      </div>
      {shouldBlockWalletClick && (
        <div
          data-testid="express-checkout-zip-blocker"
          className="absolute inset-0 w-full h-full z-[2147483647]"
          onPointerDown={handleBlockedWalletClick}
          onClick={handleBlockedWalletClick}
        />
      )}
    </div>
  );
};
